import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAuctionByAktenzeichen, fetchBidsForAuction, placeBid } from '@/services/api';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type AuctionRow = Database['public']['Tables']['auctions']['Row'];
type BidRow = Database['public']['Tables']['bids']['Row'];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

export function BidPage() {
  const { aktenzeichen } = useParams<{ aktenzeichen: string }>();
  const navigate = useNavigate();
  const [auction, setAuction] = useState<AuctionRow | null>(null);
  const [bids, setBids] = useState<BidRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!aktenzeichen) return;

    async function load() {
      const data = await fetchAuctionByAktenzeichen(aktenzeichen);
      if (!data) {
        navigate('/', { replace: true });
        return;
      }
      setAuction(data);

      const bidData = await fetchBidsForAuction(data.id);
      setBids(bidData);
      setLoading(false);
    }

    load();
  }, [aktenzeichen]);

  // Realtime subscription for live bid updates
  useEffect(() => {
    if (!auction) return;

    const subscription = supabase
      .channel(`bids-${auction.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bids', filter: `auction_id=eq.${auction.id}` },
        () => {
          fetchBidsForAuction(auction.id).then(setBids);
        }
      )
      .subscribe();

    return () => { subscription.unsubscribe(); };
  }, [auction?.id]);

  if (loading) return <div className="max-w-content mx-auto py-12"><p className="text-text-secondary">Laden...</p></div>;
  if (!auction) return null;

  if (auction.status !== 'LAUFEND' || new Date(auction.end_at) <= new Date()) {
    return (
      <div className="max-w-content mx-auto py-12">
        <h1 className="text-2xl font-serif mb-4">Auktion nicht laufend</h1>
        <p className="text-text-secondary">Diese Auktion ist derzeit nicht offen fur Gebote.</p>
      </div>
    );
  }

  const currentBid = bids[0]?.betrag || auction.mindestgebot;
  const minBid = currentBid + auction.mindest_schrittweite;

  async function handleBid(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const amount = parseFloat(bidAmount);
    if (isNaN(amount)) { setError('Bitte geben Sie einen guiltigen Betrag ein.'); return; }
    if (amount < minBid) { setError(`Mindestgebot: ${formatCurrency(minBid)}.`); return; }

    setShowConfirm(true);
  }

  async function confirmBid() {
    setShowConfirm(false);
    setSubmitting(true);

    const amount = parseFloat(bidAmount);
    const result = await placeBid(auction.id, amount);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    setSubmitting(false);
  }

  if (success) {
    return (
      <div className="max-w-content mx-auto py-12">
        <h1 className="text-2xl font-serif mb-4">Gebot abgegeben</h1>
        <p className="text-text-secondary mb-6">Ihr Gebot von {formatCurrency(parseFloat(bidAmount))} wurde registriert.</p>
        <button onClick={() => navigate(`/auktion/${aktenzeichen}`)} className="btn-primary">
          Zuruck zur Auktion
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-content mx-auto py-12">
      <h1 className="text-2xl font-serif mb-6">Gebot abgeben — {auction.titel}</h1>

      <div className="p-4 border border-surface-border rounded-sm mb-6">
        <p><strong>Aktuelles Höchstgebot:</strong> {formatCurrency(currentBid)}</p>
        <p><strong>Mindestgebot:</strong> {formatCurrency(minBid)}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/20 text-status-danger text-sm rounded-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleBid} className="space-y-4 max-w-md">
        <div>
          <label htmlFor="bidAmount" className="block text-sm font-medium mb-1">Ihr Gebot (EUR)</label>
          <input
            id="bidAmount"
            type="number"
            step="0.01"
            className="input-field"
            placeholder={formatCurrency(minBid)}
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="btn-primary" disabled={submitting}>
          Gebot abgeben
        </button>
      </form>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface p-6 rounded-sm max-w-md w-full mx-4 shadow-lg">
            <h2 className="text-lg font-serif mb-4">Gebot bestatigen</h2>
            <p className="text-text-secondary text-sm mb-4">
              Sie bieten <strong>{formatCurrency(parseFloat(bidAmount))}</strong> fur {auction.titel}.
              Dieses Gebot ist rechtsverbindlich und unwiderruflich.
            </p>
            <div className="flex gap-3">
              <button onClick={confirmBid} className="btn-primary" disabled={submitting}>
                Ja, rechtsverbindlich bieten
              </button>
              <button onClick={() => setShowConfirm(false)} className="btn-secondary">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
